import { Injectable } from '@angular/core';
import { Observable, switchMap } from 'rxjs';
import { orderBy, where } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  ParticipantSummary,
  TechnificationEnrollment,
  TechnificationEvaluation,
  TechnificationGoal,
  TechnificationProgram,
  TechnificationRole,
  TechnificationSession,
  TechnificationWellnessEntry,
} from '@models';
import { FirestoreService } from './firestore.service';
import { TenantContextService } from './tenant-context.service';

/**
 * Gestión del módulo de tecnificación: programas, sesiones, evaluaciones,
 * objetivos individuales y autoevaluaciones (wellness) de los deportistas.
 *
 * La autorización fina (coach/manager por programa) se resuelve en Firestore
 * rules vía los arrays `coachIds`/`managerIds`; este servicio solo construye
 * las consultas/escrituras.
 */
@Injectable({ providedIn: 'root' })
export class TechnificationService {
  constructor(
    private readonly firestore: FirestoreService,
    private readonly tenantContext: TenantContextService,
    private readonly functions: Functions,
  ) {}

  // ─── Programas ─────────────────────────────────────────────────────────────

  /** Programas de una temporada (orden cronológico). */
  programsBySeason$(seasonId: string): Observable<TechnificationProgram[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<TechnificationProgram>(
          this.tenantContext.collections.TECHNIFICATION_PROGRAMS,
          where('seasonId', '==', seasonId),
          orderBy('startDate', 'desc'),
        ),
      ),
    );
  }

  /** Todos los programas (histórico incluido). */
  allPrograms$(): Observable<TechnificationProgram[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<TechnificationProgram>(
          this.tenantContext.collections.TECHNIFICATION_PROGRAMS,
          orderBy('startDate', 'desc'),
        ),
      ),
    );
  }

  program$(programId: string): Observable<TechnificationProgram> {
    return this.firestore.getDocument<TechnificationProgram>(
      this.tenantContext.collections.TECHNIFICATION_PROGRAMS,
      programId,
    );
  }

  createProgram(data: Omit<TechnificationProgram, 'id'>): Promise<string> {
    return this.firestore.create<TechnificationProgram>(
      this.tenantContext.collections.TECHNIFICATION_PROGRAMS,
      data,
    );
  }

  updateProgram(
    data: Partial<TechnificationProgram> & { id: string },
  ): Promise<void> {
    return this.firestore.update<TechnificationProgram>(
      this.tenantContext.collections.TECHNIFICATION_PROGRAMS,
      data,
    );
  }

  deleteProgram(id: string): Promise<void> {
    return this.firestore.delete(
      this.tenantContext.collections.TECHNIFICATION_PROGRAMS,
      id,
    );
  }

  // ─── Sesiones ──────────────────────────────────────────────────────────────

  sessions$(programId: string): Observable<TechnificationSession[]> {
    return this.firestore.listDocuments<TechnificationSession>(
      this.tenantContext.collections.technificationSessions(programId),
      orderBy('date', 'desc'),
    );
  }

  session$(programId: string, sessionId: string): Observable<TechnificationSession> {
    return this.firestore.getDocument<TechnificationSession>(
      this.tenantContext.collections.technificationSessions(programId),
      sessionId,
    );
  }

  createSession(
    programId: string,
    data: Omit<TechnificationSession, 'id'>,
  ): Promise<string> {
    return this.firestore.create<TechnificationSession>(
      this.tenantContext.collections.technificationSessions(programId),
      data,
    );
  }

  updateSession(
    programId: string,
    data: Partial<TechnificationSession> & { id: string },
  ): Promise<void> {
    return this.firestore.update<TechnificationSession>(
      this.tenantContext.collections.technificationSessions(programId),
      data,
    );
  }

  deleteSession(programId: string, id: string): Promise<void> {
    return this.firestore.delete(
      this.tenantContext.collections.technificationSessions(programId),
      id,
    );
  }

  // ─── Evaluaciones ──────────────────────────────────────────────────────────

  evaluations$(programId: string): Observable<TechnificationEvaluation[]> {
    return this.firestore.listDocuments<TechnificationEvaluation>(
      this.tenantContext.collections.technificationEvaluations(programId),
      orderBy('date', 'desc'),
    );
  }

  evaluationsByParticipant$(
    programId: string,
    userId: string,
  ): Observable<TechnificationEvaluation[]> {
    return this.firestore.listDocuments<TechnificationEvaluation>(
      this.tenantContext.collections.technificationEvaluations(programId),
      where('userId', '==', userId),
      orderBy('date', 'asc'),
    );
  }

  evaluation$(
    programId: string,
    evaluationId: string,
  ): Observable<TechnificationEvaluation> {
    return this.firestore.getDocument<TechnificationEvaluation>(
      this.tenantContext.collections.technificationEvaluations(programId),
      evaluationId,
    );
  }

  createEvaluation(
    programId: string,
    data: Omit<TechnificationEvaluation, 'id'>,
  ): Promise<string> {
    return this.firestore.create<TechnificationEvaluation>(
      this.tenantContext.collections.technificationEvaluations(programId),
      data,
    );
  }

  updateEvaluation(
    programId: string,
    data: Partial<TechnificationEvaluation> & { id: string },
  ): Promise<void> {
    return this.firestore.update<TechnificationEvaluation>(
      this.tenantContext.collections.technificationEvaluations(programId),
      data,
    );
  }

  deleteEvaluation(programId: string, id: string): Promise<void> {
    return this.firestore.delete(
      this.tenantContext.collections.technificationEvaluations(programId),
      id,
    );
  }

  // ─── Objetivos individuales ────────────────────────────────────────────────

  goals$(programId: string): Observable<TechnificationGoal[]> {
    return this.firestore.listDocuments<TechnificationGoal>(
      this.tenantContext.collections.technificationGoals(programId),
      orderBy('createdAt', 'desc'),
    );
  }

  goalsByParticipant$(
    programId: string,
    userId: string,
  ): Observable<TechnificationGoal[]> {
    return this.firestore.listDocuments<TechnificationGoal>(
      this.tenantContext.collections.technificationGoals(programId),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
    );
  }

  createGoal(
    programId: string,
    data: Omit<TechnificationGoal, 'id'>,
  ): Promise<string> {
    return this.firestore.create<TechnificationGoal>(
      this.tenantContext.collections.technificationGoals(programId),
      data,
    );
  }

  updateGoal(
    programId: string,
    data: Partial<TechnificationGoal> & { id: string },
  ): Promise<void> {
    return this.firestore.update<TechnificationGoal>(
      this.tenantContext.collections.technificationGoals(programId),
      data,
    );
  }

  deleteGoal(programId: string, id: string): Promise<void> {
    return this.firestore.delete(
      this.tenantContext.collections.technificationGoals(programId),
      id,
    );
  }

  // ─── Inscripciones (app del jugador) ───────────────────────────────────────

  /** Inscripciones activas de un jugador (consulta rápida para la app). */
  enrollmentsByUser$(userId: string): Observable<TechnificationEnrollment[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<TechnificationEnrollment>(
          this.tenantContext.collections.TECHNIFICATION_ENROLLMENTS,
          where('userId', '==', userId),
          where('active', '==', true),
        ),
      ),
    );
  }

  // ─── Wellness / autoevaluación del deportista ──────────────────────────────

  wellnessByUser$(
    userId: string,
    days = 30,
  ): Observable<TechnificationWellnessEntry[]> {
    const since = new Date();
    since.setDate(since.getDate() - days);
    const sinceStr = since.toISOString().slice(0, 10);
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<TechnificationWellnessEntry>(
          this.tenantContext.collections.TECHNIFICATION_WELLNESS,
          where('userId', '==', userId),
          where('date', '>=', sinceStr),
          orderBy('date', 'asc'),
        ),
      ),
    );
  }

  /**
   * Crea o actualiza el reporte de bienestar del día. Id determinista
   * `${userId}_${date}` para garantizar un único reporte por día.
   */
  saveWellness(
    entry: Omit<TechnificationWellnessEntry, 'id'>,
  ): Promise<void> {
    const id = `${entry.userId}_${entry.date}`;
    return this.firestore.createOrUpdate<TechnificationWellnessEntry>(
      this.tenantContext.collections.TECHNIFICATION_WELLNESS,
      id,
      { ...entry, id },
    );
  }

  // ─── Cloud Functions ───────────────────────────────────────────────────────

  /** Resumen de asistencia y evolución de scores de un participante. */
  async getSummary(
    programId: string,
    userId: string,
  ): Promise<ParticipantSummary> {
    const fn = httpsCallable<
      { programId: string; userId: string },
      ParticipantSummary
    >(this.functions, 'getTechnificationSummary');
    const res = await fn({ programId, userId });
    return res.data;
  }

  /** Asigna un rol secundario de tecnificación a un usuario (vía additionalRoles). */
  async assignRole(userId: string, role: TechnificationRole): Promise<void> {
    const fn = httpsCallable<
      { userId: string; role: TechnificationRole },
      { success: boolean }
    >(this.functions, 'assignTechnificationRole');
    await fn({ userId, role });
  }

  async revokeRole(userId: string, role: TechnificationRole): Promise<void> {
    const fn = httpsCallable<
      { userId: string; role: TechnificationRole },
      { success: boolean }
    >(this.functions, 'revokeTechnificationRole');
    await fn({ userId, role });
  }
}
